// Préférences personnelles (S04, charte D-038) : affichage (thème Jour, Nuit ou Automatique en cartes,
// densité, taille du texte), notifications, sécurité du compte (mot de passe, second facteur), sessions, vues enregistrées.
// Les préférences sont enregistrées sur le serveur (R-11) et s'appliquent sur tous les appareils.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DENSITES_AFFICHAGE, TAILLES_TEXTE, type Preferences as TypePreferences, type TailleTexte, type Theme } from '@carburanttrack/shared';
import { api } from '../../api/client';
import { CLE_ETAT, useSession } from '../../app/session';
import { appliquerDensite, appliquerTaille, appliquerTheme } from '../../app/theme';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { dateHeure } from '../../composants/format';
import { ChangementMotDePasse, EnrolementMfa } from '../auth/Connexion';

const CHOIX_THEMES: { theme: Theme; libelle: string; detail: string }[] = [
  { theme: 'jour', libelle: 'Jour', detail: 'Fond clair, pour le bureau' },
  { theme: 'nuit', libelle: 'Nuit', detail: 'Fond sombre, pour la veille' },
  { theme: 'systeme', libelle: 'Automatique', detail: 'Suit le réglage de l’appareil' },
];
const LIBELLES_TAILLES: Record<TailleTexte, string> = { petite: 'Petite', normale: 'Normale', grande: 'Grande' };
const LIBELLES_NIVEAUX = { informative: 'Informative', preventive: 'Préventive', elevee: 'Élevée', critique: 'Critique' };
const SECTIONS = [
  ['affichage', 'Affichage'],
  ['notifications', 'Notifications'],
  ['securite', 'Sécurité du compte'],
  ['sessions', 'Sessions ouvertes'],
  ['vues', 'Vues enregistrées'],
] as const;

/** Aperçu d'un thème : les jetons du thème sont appliqués à l'aperçu lui-même (attribut data-theme). */
function ApercuTheme({ theme }: { theme: Theme }) {
  if (theme === 'systeme') {
    return (
      <span className="apercu-theme double" aria-hidden="true">
        <span data-theme="jour">
          <i />
          <i />
        </span>
        <span data-theme="nuit">
          <i />
          <i />
        </span>
      </span>
    );
  }
  return (
    <span className="apercu-theme" aria-hidden="true">
      <span data-theme={theme}>
        <i />
        <i />
      </span>
    </span>
  );
}

export function PagePreferences() {
  const client = useQueryClient();
  const { etat, recharger } = useSession();
  const [section, setSection] = useState<string>('affichage');
  const prefs = useQuery({ queryKey: ['preferences'], queryFn: async () => (await api<TypePreferences>('/api/v1/preferences')).donnees });
  const enregistrement = useMutation({
    mutationFn: async (p: TypePreferences) => (await api<TypePreferences>('/api/v1/preferences', { methode: 'PUT', corps: p })).donnees,
    onMutate: (p) => {
      // Effet immédiat, sans attendre la réponse ; le serveur reste la référence.
      appliquerTheme(p.theme);
      appliquerDensite(p.densite);
      appliquerTaille(p.tailleTexte ?? 'normale');
    },
    onSuccess: async (p) => {
      client.setQueryData(['preferences'], p);
      await client.invalidateQueries({ queryKey: CLE_ETAT });
    },
  });
  if (prefs.isLoading) return <Squelette lignes={6} />;
  if (prefs.error) return <MessageErreur erreur={prefs.error} />;
  const p = prefs.data!;
  const modifier = (changement: Partial<TypePreferences>) => enregistrement.mutate({ ...p, ...changement });
  const mfaActif = Boolean(etat?.utilisateur?.mfaActif);

  return (
    <div className="avec-menu page-preferences">
      <nav className="menu-lateral" aria-label="Sections des préférences">
        <h1>Préférences</h1>
        <ul>
          {SECTIONS.map(([ancre, libelle]) => (
            <li key={ancre}>
              <a href={`#${ancre}`} aria-current={section === ancre ? 'page' : undefined} onClick={() => setSection(ancre)}>
                {libelle}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="menu-contenu">
        {enregistrement.error && <MessageErreur erreur={enregistrement.error} titre="Enregistrement refusé" />}
        {enregistrement.isSuccess && <Message type="succes">Préférences enregistrées sur le serveur ; elles s’appliquent sur tous vos appareils.</Message>}

        <section id="affichage" className="carte-bloc reglages" aria-labelledby="titre-affichage">
          <h2 id="titre-affichage">Affichage</h2>
          <div className="reglage-preference">
            <span id="libelle-theme" className="libelle-reglage">
              Thème
            </span>
            <div className="cartes-choix" role="radiogroup" aria-labelledby="libelle-theme">
              {CHOIX_THEMES.map((c) => (
                <button key={c.theme} type="button" role="radio" className="carte-choix" aria-checked={p.theme === c.theme} onClick={() => modifier({ theme: c.theme })}>
                  <ApercuTheme theme={c.theme} />
                  <span className="texte">
                    <b>{c.libelle}</b>
                    <small>{c.detail}</small>
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="reglage-preference">
            <span id="libelle-densite" className="libelle-reglage">
              Densité d’affichage
            </span>
            <div className="segments" role="radiogroup" aria-labelledby="libelle-densite">
              {DENSITES_AFFICHAGE.map((d) => (
                <button key={d} type="button" role="radio" aria-checked={p.densite === d} onClick={() => modifier({ densite: d })}>
                  {d === 'confortable' ? 'Confortable' : 'Compacte'}
                </button>
              ))}
            </div>
          </div>
          <div className="reglage-preference">
            <span id="libelle-taille" className="libelle-reglage">
              Taille du texte
            </span>
            <div className="segments" role="radiogroup" aria-labelledby="libelle-taille">
              {TAILLES_TEXTE.map((t) => (
                <button key={t} type="button" role="radio" aria-checked={(p.tailleTexte ?? 'normale') === t} onClick={() => modifier({ tailleTexte: t })}>
                  {LIBELLES_TAILLES[t]}
                </button>
              ))}
            </div>
          </div>
          <p className="attenue">Langue : français.</p>
        </section>

        <section id="notifications" className="carte-bloc reglages" aria-labelledby="titre-notifications">
          <h2 id="titre-notifications">Notifications</h2>
          <label className="case">
            <input type="checkbox" checked={p.notifications.interne} onChange={(e) => modifier({ notifications: { ...p.notifications, interne: e.target.checked } })} />
            <span>Dans l’application</span>
          </label>
          <label className="case">
            <input type="checkbox" checked={p.notifications.courriel} onChange={(e) => modifier({ notifications: { ...p.notifications, courriel: e.target.checked } })} />
            <span>
              Par courriel
              <small>Fournisseur non raccordé : aucun envoi tant qu’il n’est pas configuré.</small>
            </span>
          </label>
          <label className="case">
            <input type="checkbox" checked={p.notifications.sms} onChange={(e) => modifier({ notifications: { ...p.notifications, sms: e.target.checked } })} />
            <span>
              Par SMS
              <small>Fournisseur non raccordé : aucun envoi tant qu’il n’est pas configuré.</small>
            </span>
          </label>
          <div className="champ champ-etroit">
            <label htmlFor="niveau-min">Niveau d’alerte minimal notifié</label>
            <select
              id="niveau-min"
              value={p.notifications.niveauMinimal}
              onChange={(e) => modifier({ notifications: { ...p.notifications, niveauMinimal: e.target.value as TypePreferences['notifications']['niveauMinimal'] } })}
            >
              {Object.entries(LIBELLES_NIVEAUX).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
        </section>

        <div id="securite" className="grille-2">
          <section className="carte-bloc" aria-labelledby="titre-mot-de-passe">
            <h2 id="titre-mot-de-passe">Mot de passe</h2>
            <ChangementMotDePasse onTermine={recharger} impose={false} />
          </section>
          <section className={mfaActif ? 'carte-bloc' : 'carte-bloc carte-recommandee'} aria-labelledby="titre-mfa">
            {!mfaActif && <span className="sur-titre">Recommandé</span>}
            <h2 id="titre-mfa">Second facteur</h2>
            {mfaActif ? (
              <p>Second facteur actif. En cas de perte du téléphone, utilisez un code de secours ou demandez une réinitialisation à l’administrateur national.</p>
            ) : (
              <>
                <p className="attenue">Non activé.</p>
                <EnrolementMfa onTermine={recharger} integre />
              </>
            )}
          </section>
        </div>

        <Sessions />
        <VuesEnregistrees />
      </div>
    </div>
  );
}

function Sessions() {
  const { recharger } = useSession();
  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: async () => (await api<{ id: string; creeLe: string; derniereActiviteLe: string; adresse: string | null; agentUtilisateur: string | null; courante: boolean }[]>('/api/v1/auth/sessions')).donnees,
  });
  const revocation = useMutation({
    mutationFn: () => api<{ sessionsRevoquees: number }>('/api/v1/auth/sessions/revocation', { corps: {} }),
    onSuccess: () => recharger(),
  });
  const [toutes, setToutes] = useState(false);
  // Cette session d'abord, puis les plus récentes ; au-delà de cinq, les autres sont repliées.
  const triees = [...(sessions.data ?? [])].sort((a, b) => Number(b.courante) - Number(a.courante) || b.derniereActiviteLe.localeCompare(a.derniereActiviteLe));
  const affichees = toutes ? triees : triees.slice(0, 5);
  return (
    <section id="sessions" className="carte-bloc" aria-labelledby="titre-sessions">
      <h2 id="titre-sessions">Sessions ouvertes</h2>
      {sessions.isLoading && <Squelette lignes={2} />}
      {sessions.data && (
        <div className="tableau-conteneur">
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Session</th>
                <th scope="col">Ouverte le</th>
                <th scope="col" className="masquable">
                  Dernière activité
                </th>
                <th scope="col" className="masquable">
                  Adresse
                </th>
              </tr>
            </thead>
            <tbody>
              {affichees.map((s) => (
                <tr key={s.id}>
                  <td>{s.courante ? <b>Cette session</b> : 'Autre session'}</td>
                  <td>{dateHeure(s.creeLe)}</td>
                  <td className="masquable">{dateHeure(s.derniereActiviteLe)}</td>
                  <td className="masquable">{s.adresse ?? <span className="attenue">non enregistrée</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {revocation.error && <MessageErreur erreur={revocation.error} />}
      <div className="actions espace-haut">
        {triees.length > 5 && (
          <button type="button" className="bouton" aria-expanded={toutes} onClick={() => setToutes((v) => !v)}>
            {toutes ? 'Replier la liste' : `Afficher les ${triees.length - 5} autres sessions`}
          </button>
        )}
        <button type="button" className="bouton danger" onClick={() => revocation.mutate()}>
          Fermer toutes mes sessions, y compris celle-ci
        </button>
      </div>
    </section>
  );
}

function VuesEnregistrees() {
  const client = useQueryClient();
  const vues = useQuery({ queryKey: ['vues'], queryFn: async () => (await api<{ id: string; nom: string; ecran: string; filtres: Record<string, string> }[]>('/api/v1/vues')).donnees });
  const [nom, setNom] = useState('');
  const creation = useMutation({
    mutationFn: () => {
      const filtres = Object.fromEntries(new URLSearchParams(window.sessionStorage.getItem('ct-derniere-recherche-installations') ?? ''));
      return api('/api/v1/vues', { corps: { nom, ecran: 'installations', filtres } });
    },
    onSuccess: async () => {
      setNom('');
      await client.invalidateQueries({ queryKey: ['vues'] });
    },
  });
  const archivage = useMutation({
    mutationFn: (id: string) => api(`/api/v1/vues/${id}/archivage`, { corps: {} }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['vues'] }),
  });
  return (
    <section id="vues" className="carte-bloc" aria-labelledby="titre-vues">
      <h2 id="titre-vues">Vues enregistrées</h2>
      {vues.data?.length === 0 && <p className="attenue">Aucune vue enregistrée.</p>}
      {vues.data && vues.data.length > 0 && (
        <ul className="liste-vues">
          {vues.data.map((v) => {
            const requete = new URLSearchParams(v.filtres).toString();
            return (
              <li key={v.id}>
                <a href={`/referentiel/installations${requete ? `?${requete}` : ''}`}>{v.nom}</a>
                <button type="button" className="bouton" onClick={() => archivage.mutate(v.id)}>
                  Retirer de mes vues
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <form
        className="formulaire-vue"
        onSubmit={(e) => {
          e.preventDefault();
          creation.mutate();
        }}
      >
        <div className="champ">
          <label htmlFor="vue-nom">Enregistrer les derniers filtres de la liste des installations sous le nom</label>
          <input id="vue-nom" placeholder="Par exemple : stations de Libreville" value={nom} onChange={(e) => setNom(e.target.value)} />
        </div>
        <button type="submit" className="bouton" disabled={!nom.trim()}>
          Enregistrer la vue
        </button>
      </form>
      {creation.error && <MessageErreur erreur={creation.error} />}
    </section>
  );
}
